// components/LayerSelectionFrame.tsx
import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { buildLiveScreenSize, buildLiveScreenX, buildLiveScreenY, computeWorldSizeFromIntrinsic } from '#lib/layer-geometry';
import { resolveStudioLayerTransform } from '#lib/studio-layer-transform';

// Figma-style selection chrome: a thin blue wireframe around the layer plus a
// dimension pill centred just below it. Rendered in the screen-space canvas
// container via the live viewport CSS variables so the wireframe tracks zoom/pan
// while its stroke remains a constant screen-pixel size.
const SELECTION_BLUE = '#3B82F6';
const SELECTION_FRAME_MAX_OPACITY = 0.5;
const PILL_GAP_PX = 12;

// Static halves of the two portal styles, hoisted out of the render: the frame
// re-renders on every pan/zoom frame and only the geometry keys below actually
// change.
const BOX_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  boxSizing: 'border-box',
  borderStyle: 'solid',
  borderColor: SELECTION_BLUE,
  borderWidth: '6px',
  opacity: 0,
  transformOrigin: '50% 50%',
  pointerEvents: 'none',
  zIndex: 40,
};

// Anchored to the (un-rotated) bottom-centre, kept a constant screen size and
// a constant gap below the box regardless of zoom.
const PILL_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  transform: `translate(-50%, ${PILL_GAP_PX}px)`,
  transformOrigin: '0 0',
  background: SELECTION_BLUE,
  color: '#ffffff',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: 1,
  letterSpacing: '0.2px',
  padding: '4px 8px',
  borderRadius: '5px',
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
  boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
  pointerEvents: 'none',
  zIndex: 42,
};

interface LayerSelectionFrameProps {
  layerId: string;
  showDimensionPill?: boolean;
}

const LayerSelectionFrame: React.FC<LayerSelectionFrameProps> = React.memo(({ layerId, showDimensionPill = true }) => {
  const { kernel } = useKernel();
  const wireframeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const wireframe = wireframeRef.current;
    if (!wireframe) return;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    wireframe.style.opacity = prefersReducedMotion ? '0.35' : '0';

    if (prefersReducedMotion || typeof wireframe.animate !== 'function') {
      return;
    }

    const animation = wireframe.animate(
      [
        { opacity: 0, offset: 0 },
        { opacity: SELECTION_FRAME_MAX_OPACITY, offset: 0.5 },
        { opacity: 0, offset: 1 },
      ],
      {
        duration: 3200,
        easing: 'ease-in-out',
        iterations: Infinity,
      }
    );

    return () => {
      animation.cancel();
    };
  }, []);
  const layerExists = useKernelStore((s) => s.inventory.StudioLayer.registry[layerId] != null);
  const layerWorkspaceId = useKernelStore((s) => s.inventory.StudioLayer.registry[layerId]?.workspaceId ?? '');
  const panOffsetX = useKernelStore((s) => {
    const ws = s.inventory.StudioLayer.registry[layerId]?.workspaceId;
    const projectId = ws ? s.inventory.StudioWorkspace.registry[ws]?.projectId : undefined;
    const value = projectId ? s.inventory.Project.registry[projectId]?.runtime?.panOffset?.x : undefined;
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  });
  const panOffsetY = useKernelStore((s) => {
    const ws = s.inventory.StudioLayer.registry[layerId]?.workspaceId;
    const projectId = ws ? s.inventory.StudioWorkspace.registry[ws]?.projectId : undefined;
    const value = projectId ? s.inventory.Project.registry[projectId]?.runtime?.panOffset?.y : undefined;
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  });
  const zoomLevel = useKernelStore((s) => {
    const ws = s.inventory.StudioLayer.registry[layerId]?.workspaceId;
    const projectId = ws ? s.inventory.StudioWorkspace.registry[ws]?.projectId : undefined;
    const value = projectId ? s.inventory.Project.registry[projectId]?.runtime?.zoomLevel : undefined;
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 1;
  });
  const frameX = useKernelStore((s) => {
    const ws = s.inventory.StudioLayer.registry[layerId]?.workspaceId;
    return ws ? s.inventory.StudioWorkspace.registry[ws]?.frameRect?.x : undefined;
  });
  const frameY = useKernelStore((s) => {
    const ws = s.inventory.StudioLayer.registry[layerId]?.workspaceId;
    return ws ? s.inventory.StudioWorkspace.registry[ws]?.frameRect?.y : undefined;
  });
  const rectX = useKernelStore((s) => s.inventory.StudioLayer.registry[layerId]?.rect.x);
  const rectY = useKernelStore((s) => s.inventory.StudioLayer.registry[layerId]?.rect.y);
  const rectW = useKernelStore((s) => s.inventory.StudioLayer.registry[layerId]?.rect.w);
  const rectH = useKernelStore((s) => s.inventory.StudioLayer.registry[layerId]?.rect.h);
  const scale = useKernelStore((s) => s.inventory.StudioLayer.registry[layerId]?.scale ?? 1);
  const imageWidth = useKernelStore((s) => s.inventory.StudioLayer.registry[layerId]?.imageData?.width);
  const imageHeight = useKernelStore((s) => s.inventory.StudioLayer.registry[layerId]?.imageData?.height);
  const layerParams = useKernelStore((s) => s.inventory.StudioLayer.registry[layerId]?.params);

  const geometry = useMemo(() => {
    if (!layerExists || !layerWorkspaceId) return null;
    if (typeof rectX !== 'number' || typeof rectY !== 'number') return null;
    if (typeof frameX !== 'number' || typeof frameY !== 'number') return null;
    let w: number | null = typeof rectW === 'number' && Number.isFinite(rectW) && rectW > 0 ? rectW : null;
    let h: number | null = typeof rectH === 'number' && Number.isFinite(rectH) && rectH > 0 ? rectH : null;
    if (w === null || h === null) {
      const intrinsic = computeWorldSizeFromIntrinsic(imageWidth ?? NaN, imageHeight ?? NaN, scale);
      if (!intrinsic) return null;
      w = intrinsic.w;
      h = intrinsic.h;
    }
    return {
      worldX: frameX + rectX,
      worldY: frameY + rectY,
      w,
      h,
      rotationDeg: resolveStudioLayerTransform(layerParams).rotationDeg,
    };
  }, [layerExists, layerWorkspaceId, rectX, rectY, rectW, rectH, frameX, frameY, imageWidth, imageHeight, scale, layerParams]);

  if (!geometry) return null;
  const { worldX, worldY, w, h, rotationDeg } = geometry;

  const boxStyle: React.CSSProperties = {
    ...BOX_BASE_STYLE,
    width: buildLiveScreenSize(w, zoomLevel),
    height: buildLiveScreenSize(h, zoomLevel),
    transform: `rotate(${rotationDeg}deg)`,
    left: buildLiveScreenX(worldX, panOffsetX, zoomLevel),
    top: buildLiveScreenY(worldY, panOffsetY, zoomLevel),
  };

  const pillStyle: React.CSSProperties = {
    ...PILL_BASE_STYLE,
    left: buildLiveScreenX(worldX + w / 2, panOffsetX, zoomLevel),
    top: buildLiveScreenY(worldY + h, panOffsetY, zoomLevel),
  };

  const portalHost = kernel.current?.canvasContainerRef.current ?? null;
  if (!portalHost) return null;

  return createPortal(
    <>
      <div ref={wireframeRef} style={boxStyle} />
      {showDimensionPill && <div style={pillStyle}>{`${Math.round(w)} × ${Math.round(h)}`}</div>}
    </>,
    portalHost
  );
});

LayerSelectionFrame.displayName = 'LayerSelectionFrame';

export default LayerSelectionFrame;
